import { Brain } from 'lucide-react';
import { useMemo } from 'react';

import type { IStep } from '@chainlit/react-client';

interface Props {
  step: IStep;
  isRunning?: boolean;
}

const TAIL_CHAR_COUNT = 100;

/**
 * Return the most recent non-empty line of raw streaming output so the user
 * sees the current thought in progress instead of a rolling wall of text.
 * Walks backwards from the tail by scanning for newline boundaries so long
 * traces do not cost an O(n) split on every token flush.
 */
function latestLine(raw: string): string {
  if (!raw) return '';
  let end = raw.length;
  while (end > 0) {
    const nl = raw.lastIndexOf('\n', end - 1);
    const line = raw.slice(nl + 1, end).trim();
    if (line) return line;
    if (nl < 0) return '';
    end = nl;
  }
  return '';
}

export const THINKING_STEP_NAME = 'Thinking';

/**
 * Whether a Thinking step is still producing tokens.
 *
 * Shared with `Message` so the surrounding step row and avatar are skipped on
 * exactly the same condition that makes this component render nothing.
 */
export function isThinkingStreaming(step: IStep, isRunning?: boolean): boolean {
  if (typeof step.streaming === 'boolean') return step.streaming;
  return Boolean(isRunning && step.start && !step.end && !step.isError);
}

/**
 * Live-preview for a ``cl.Step(name="Thinking")`` parented to an assistant
 * message. Renders reasoning tokens inline as grayed, italic text above the
 * final answer.
 *
 * While streaming, a rolling tail of the latest tokens is shown with a
 * pulsing cursor. Once the step finalizes it renders nothing, leaving only
 * the assistant message in the chat. The step itself is untouched, so the
 * trace stays in the thread record.
 */
export default function ThinkingStream({ step, isRunning }: Props) {
  const streaming = useMemo(
    () => isThinkingStreaming(step, isRunning),
    [step.streaming, isRunning, step.start, step.end, step.isError]
  );

  const rawOutput = typeof step.output === 'string' ? step.output : '';
  const output = rawOutput.trim();

  if (streaming) {
    const streamingText = latestLine(rawOutput).slice(-TAIL_CHAR_COUNT);
    return (
      <div className="flex flex-col gap-1 min-w-0 animate-thinking-fade">
        <div className="cr-thinking-summary">
          <Brain className="h-3 w-3" />
          <span>Thinking{output ? '...' : ''}</span>
        </div>
        <div className="cr-thinking-stream streaming">
          {streamingText || ' '}
        </div>
      </div>
    );
  }

  return null;
}
